"use client";

import {
  diatonicChordsForKey,
  type DiatonicChord,
} from "@/lib/music/chord-catalog";
import { useChordLabel, useNoteLabel } from "@/hooks/use-notation";
import {
  BORDER,
  FONT_MONO,
  SURFACE,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
} from "@/components/ui/organisms/chord-practice/palette";
import type { HarmonyFocus, HarmonyTonality } from "./types";
import {
  DIATONIC_FUNCTION,
  FUNCTION_COLOR,
  POPULAR_PROGRESSIONS,
} from "./harmony-data";

interface HarmonyAtlasProps {
  tonality: HarmonyTonality;
  focus: HarmonyFocus;
  onFocusChange: (focus: HarmonyFocus) => void;
}

/**
 * Sidebar Harmony : Tonalité → Gamme → Accords diatoniques (T/SD/D) →
 * Progressions clés. Pilote le focus consommé par le stage central.
 */
export function HarmonyAtlas({
  tonality,
  focus,
  onFocusChange,
}: HarmonyAtlasProps) {
  const toNoteLabel = useNoteLabel();
  const diatonic = diatonicChordsForKey(tonality.tonic, tonality.mode);
  const tonicLabel = `${toNoteLabel(tonality.tonic)} ${
    tonality.mode === "major" ? "majeur" : "mineur"
  }`;

  return (
    <aside
      style={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        background: "rgba(8,8,14,0.6)",
        overflowY: "auto",
      }}
    >
      <TonicHeader
        label={tonicLabel}
        active={focus.kind === "overview"}
        onClick={() => onFocusChange({ kind: "overview" })}
      />

      <Section title="Gamme" tag="01">
        <Row
          label={tonicLabel}
          active={focus.kind === "scale"}
          onClick={() => onFocusChange({ kind: "scale" })}
          icon={<DotIcon active />}
        />
      </Section>

      <Section title="Accords diatoniques" tag="02">
        {diatonic.map((dc, i) => (
          <ChordRow
            key={dc.entry.id}
            chord={dc}
            functionTag={DIATONIC_FUNCTION[i] ?? "T"}
            active={focus.kind === "chord" && focus.degree === i}
            onClick={() => onFocusChange({ kind: "chord", degree: i })}
          />
        ))}
      </Section>

      <Section title="Progressions clés" tag="03">
        {POPULAR_PROGRESSIONS.map((p) => (
          <ProgressionRow
            key={p.id}
            numerals={p.numerals}
            degrees={p.degrees}
            hint={p.label}
            active={focus.kind === "progression" && focus.id === p.id}
            onClick={() => onFocusChange({ kind: "progression", id: p.id })}
          />
        ))}
      </Section>
    </aside>
  );
}

function TonicHeader({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: "16px 16px 14px",
        borderBottom: `1px solid ${BORDER}`,
        background: active
          ? `linear-gradient(135deg, ${VIOLET_GLOW}, transparent 70%)`
          : "transparent",
        cursor: "pointer",
        position: "relative",
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontFamily: FONT_MONO,
          color: TEXT_MUTE,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        Tonalité
      </div>
      <div
        style={{
          fontSize: 18,
          fontFamily: "Inter, sans-serif",
          fontWeight: 500,
          color: active ? VIOLET_BRIGHT : TEXT,
          letterSpacing: "-0.01em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 10,
          color: TEXT_MUTE,
          marginTop: 4,
          fontFamily: FONT_MONO,
          letterSpacing: "0.06em",
        }}
      >
        ↳ vue d&apos;ensemble
      </div>
    </button>
  );
}

function Section({
  title,
  tag,
  children,
}: {
  title: string;
  tag: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        padding: "14px 0 10px",
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <header
        style={{
          padding: "0 16px 8px",
          display: "flex",
          alignItems: "baseline",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 9,
            fontFamily: FONT_MONO,
            color: VIOLET_BRIGHT,
            opacity: 0.55,
            letterSpacing: "0.12em",
            fontWeight: 600,
          }}
        >
          {tag}
        </span>
        <span
          style={{
            fontSize: 10,
            fontFamily: FONT_MONO,
            color: TEXT_DIM,
            letterSpacing: "0.16em",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {title}
        </span>
      </header>
      <div>{children}</div>
    </section>
  );
}

function Row({
  label,
  hint,
  active,
  onClick,
  icon,
}: {
  label: string;
  hint?: string;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        textAlign: "left",
        padding: "6px 16px 6px 14px",
        background: active
          ? `linear-gradient(90deg, ${VIOLET_GLOW}, transparent)`
          : "transparent",
        borderLeft: active
          ? `2px solid ${VIOLET}`
          : "2px solid transparent",
        cursor: "pointer",
        transition: "background 120ms",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = SURFACE;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = "transparent";
        }
      }}
    >
      <span style={{ flexShrink: 0, opacity: active ? 1 : 0.5 }}>{icon}</span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 13,
            color: TEXT,
            fontFamily: "Inter, sans-serif",
            lineHeight: 1.2,
          }}
        >
          {label}
        </div>
        {hint && (
          <div
            style={{
              fontSize: 10,
              color: TEXT_MUTE,
              fontFamily: FONT_MONO,
              letterSpacing: "0.04em",
              marginTop: 2,
            }}
          >
            {hint}
          </div>
        )}
      </span>
    </button>
  );
}

function ChordRow({
  chord,
  functionTag,
  active,
  onClick,
}: {
  chord: DiatonicChord;
  functionTag: string;
  active: boolean;
  onClick: () => void;
}) {
  const chordLabel = useChordLabel();

  const tone = FUNCTION_COLOR[functionTag] ?? "rgba(196,181,253,0.85)";

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        textAlign: "left",
        padding: "5px 16px 5px 14px",
        background: active
          ? `linear-gradient(90deg, ${VIOLET_GLOW}, transparent)`
          : "transparent",
        borderLeft: active
          ? `2px solid ${VIOLET}`
          : "2px solid transparent",
        cursor: "pointer",
        transition: "background 120ms",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = SURFACE;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = "transparent";
        }
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 32,
          fontSize: 11,
          fontFamily: FONT_MONO,
          color: tone,
          fontWeight: 600,
          letterSpacing: "0.04em",
        }}
      >
        {chord.roman}
      </span>
      <span
        style={{
          fontSize: 13,
          color: TEXT,
          fontFamily: "Inter, sans-serif",
        }}
      >
        {chordLabel(chord.entry.symbol)}
      </span>
    </button>
  );
}

function ProgressionRow({
  numerals,
  degrees,
  hint,
  active,
  onClick,
}: {
  numerals: string;
  degrees: number[];
  hint: string;
  active: boolean;
  onClick: () => void;
}) {
  const parts = numerals.split(/\s+–\s+/);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        textAlign: "left",
        padding: "6px 16px 6px 14px",
        background: active
          ? `linear-gradient(90deg, ${VIOLET_GLOW}, transparent)`
          : "transparent",
        borderLeft: active
          ? `2px solid ${VIOLET}`
          : "2px solid transparent",
        cursor: "pointer",
        transition: "background 120ms",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = SURFACE;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = "transparent";
        }
      }}
    >
      <span style={{ flexShrink: 0, opacity: active ? 1 : 0.5 }}>
        <ChainIcon />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 13,
            fontFamily: "Inter, sans-serif",
            lineHeight: 1.2,
            display: "flex",
            alignItems: "baseline",
            flexWrap: "wrap",
            gap: 4,
          }}
        >
          {parts.map((part, i) => {
            const degree = degrees[i];
            const fnTag =
              degree !== undefined ? DIATONIC_FUNCTION[degree] : undefined;
            const color =
              fnTag !== undefined ? FUNCTION_COLOR[fnTag] : TEXT;

            return (
              <span
                key={i}
                style={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 4,
                }}
              >
                {i > 0 && (
                  <span style={{ color: TEXT_MUTE, opacity: 0.5 }}>–</span>
                )}
                <span
                  style={{
                    color,
                    fontFamily: FONT_MONO,
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {part}
                </span>
              </span>
            );
          })}
        </div>
        <div
          style={{
            fontSize: 10,
            color: TEXT_MUTE,
            fontFamily: FONT_MONO,
            letterSpacing: "0.04em",
            marginTop: 2,
          }}
        >
          {hint}
        </div>
      </span>
    </button>
  );
}

function DotIcon({ active = false }: { active?: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: active ? VIOLET_BRIGHT : "rgba(196,181,253,0.4)",
        boxShadow: active ? "0 0 6px rgba(196,181,253,0.6)" : "none",
      }}
    />
  );
}

function ChainIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M3 6a2 2 0 1 1 4 0M5 6a2 2 0 1 0 4 0"
        stroke="rgba(196,181,253,0.6)"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}
